/**
 * Koppeling met KC, alleen via bestaande same-origin endpoints (in KC wordt niets gebouwd):
 *   GET/POST /api/design?coid=               ontwerp-JSON van de huidige order
 *   POST     /imgbase/upload_image/version_2 afbeelding opslaan -> { sid, width, height }
 *   GET      /flat_preview/<coid>/<pagina>/<folie>/<breedte>  render van een kaartpagina
 */
import { boundsOf, coverFit, placedSize, type Point, type Rect } from './geometry';
import { EmbedError } from './errors';
import { log } from './config';
import { normalizeAiPhotoConfigId, type AiPhotoScope } from '@ai-photo-style/shared';
import { originScope } from './catalog';

export interface KcImage {
  type?: string;
  sid?: string | null;
  url?: string;
  x: number;
  y: number;
  size: number;
  aspect: number;
  clip_path?: Point[];
  clip_path_type?: string;
  has_instant_replaceable_placeholder?: boolean;
  is_instant_replaceable?: boolean;
  is_replaceable?: boolean;
  [key: string]: unknown;
}

export interface KcPage {
  w: number;
  h: number;
  images?: KcImage[];
  [key: string]: unknown;
}

export interface KcDesignResponse {
  designs: Array<{ design_id?: number; pages: Record<string, unknown> }>;
  [key: string]: unknown;
}

export interface PhotoFrame {
  /** Bijv. `p1`. */
  pageKey: string;
  /** Index in `pages[pageKey].images`. */
  index: number;
  frame: Rect;
}

export interface UploadedImage {
  sid: string;
  width: number;
  height: number;
}

export function readCoid(doc: Document = document): string | null {
  const fromBuilder = doc.getElementById('js-product-builder')?.getAttribute('coid');
  if (fromBuilder) return fromBuilder;
  const input = doc.getElementById('coid') as HTMLInputElement | null;
  if (input?.value) return input.value;
  return new URLSearchParams(location.search).get('coid');
}

export async function fetchDesign(coid: string): Promise<KcDesignResponse> {
  const res = await fetch(`/api/design?coid=${encodeURIComponent(coid)}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new EmbedError('KC_APPLY_FAILED');
  return (await res.json()) as KcDesignResponse;
}

const MAIN_IMAGE_TAGS = new Set(['main-image', 'main-img']);

function imageTag(img: KcImage): string {
  return typeof img.tag === 'string' ? img.tag.trim().toLowerCase() : '';
}

function isCustomerPhoto(img: KcImage): boolean {
  return img.type === 'photo' && !!(img.has_instant_replaceable_placeholder || img.is_instant_replaceable || img.is_replaceable);
}

/** Kader uit het clip-pad, of anders de huidige plaatsing van de foto. */
function frameOf(img: KcImage): Rect | null {
  if (Array.isArray(img.clip_path) && img.clip_path.length >= 3) return boundsOf(img.clip_path);
  if (!(img.size > 0) || !(img.aspect > 0)) return null;
  const { width, height } = placedSize({ x: img.x, y: img.y, size: img.size, aspect: img.aspect });
  return { x: img.x, y: img.y, w: width, h: height };
}

/**
 * Het fotovak waar de afbeelding in komt.
 * Staat er een foto met tag `main-image` (of `main-img`), dan alleen die.
 * Anders het grootste vervangbare fotokader, met voorkeur voor de voorkant.
 */
export function findPhotoFrame(design: KcDesignResponse): PhotoFrame | null {
  const pages = design.designs?.[0]?.pages;
  if (!pages) return null;
  const pageKeys = Object.keys(pages)
    .filter((k) => /^p\d+$/.test(k))
    .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));

  const pick = (wantTag: boolean): PhotoFrame | null => {
    let best: (PhotoFrame & { area: number }) | null = null;
    for (const pageKey of pageKeys) {
      const images = (pages[pageKey] as KcPage).images ?? [];
      images.forEach((img, index) => {
        if (!isCustomerPhoto(img)) return;
        const tagged = MAIN_IMAGE_TAGS.has(imageTag(img));
        if (wantTag !== tagged) return;
        if (!wantTag && img.clip_path_type !== 'photoFrame') return;
        const frame = frameOf(img);
        if (!frame || frame.w <= 0 || frame.h <= 0) return;
        const area = frame.w * frame.h;
        if (!best || area > best.area) best = { pageKey, index, frame, area };
      });
      if (best) break;
    }
    return best ? { pageKey: best.pageKey, index: best.index, frame: best.frame } : null;
  };

  return pick(true) ?? pick(false);
}

/** Zet de geüploade afbeelding in het fotokader (cover-fit). Kader en route blijven staan. */
export function applyImageToDesign(
  design: KcDesignResponse,
  target: PhotoFrame,
  image: UploadedImage,
): KcDesignResponse {
  const clone = structuredClone(design);
  const page = clone.designs[0]!.pages[target.pageKey] as KcPage;
  const img = page.images?.[target.index];
  if (!img) throw new EmbedError('NO_FRAME');
  const p = coverFit(target.frame, image.width, image.height);
  Object.assign(img, {
    sid: image.sid,
    url: '',
    x: p.x,
    y: p.y,
    size: p.size,
    aspect: p.aspect,
    has_instant_replaceable_placeholder: false,
  });
  return clone;
}

export async function saveDesign(coid: string, design: KcDesignResponse): Promise<void> {
  const res = await fetch(`/api/design?coid=${encodeURIComponent(coid)}`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(design),
  });
  if (!res.ok) throw new EmbedError('KC_APPLY_FAILED');
}

export async function uploadToImgbase(blob: Blob): Promise<UploadedImage> {
  const form = new FormData();
  const png = blob.type === 'image/png';
  form.append('data', blob, png ? 'illustratie.png' : 'aquarel.jpg');
  const res = await fetch('/imgbase/upload_image/version_2', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    body: form,
  });
  // 413: te groot voor deze KC-omgeving (lokaal is de nginx-limiet 1 MB; productie accepteert meer).
  if (res.status === 413) throw new EmbedError('IMAGE_TOO_LARGE');
  if (!res.ok) throw new EmbedError('KC_APPLY_FAILED');
  const json = (await res.json()) as Partial<UploadedImage>;
  if (!json.sid || !json.width || !json.height) throw new EmbedError('KC_APPLY_FAILED');
  return { sid: json.sid, width: json.width, height: json.height };
}

/**
 * Open de gewone editor van de order op deze PDP. De light editor blijft dicht.
 * Het ontwerp is al opgeslagen op dit coid, dus de illustratie staat in het fotokader.
 */
export function openFullEditor(go: (url: string) => void = (url) => location.assign(url)): boolean {
  const coid = readCoid();
  if (!coid) return false;
  go(`/create/edit/v3/?coid=${encodeURIComponent(coid)}`);
  return true;
}

// ---------------------------------------------------------------------------
// Preview op de PDP

interface CardImageUrls {
  mobile: { src: string; fallback: string };
  desktop: { src: string; fallback: string };
}

interface PreviewItem {
  component?: string;
  properties?: { cardImages?: unknown[]; cardSides?: number; [key: string]: unknown };
  [key: string]: unknown;
}

const CARD_COMPONENTS = ['sp-card-preview', 'sp-card-envelope', 'sp-card-in-envelope'];

export function flatPreviewUrl(coid: string, page: number, foil: string, width: number, t: number): string {
  return `/flat_preview/${encodeURIComponent(coid)}/${page}/${foil}/${width}?_t=${t}`;
}

/** Folie-segment uit een bestaande flat_preview-URL; anders `static_foil` (zelfde default als de light editor). */
function foilFrom(items: PreviewItem[]): string {
  const json = JSON.stringify(items);
  const m = json.match(/\/flat_preview\/[^/]+\/\d+\/([a-z_]+)\//);
  return m?.[1] ?? 'static_foil';
}

function pageCountFrom(items: PreviewItem[]): number {
  for (const item of items) {
    const props = item.properties;
    if (!props) continue;
    if (typeof props.cardSides === 'number' && props.cardSides > 0) return props.cardSides;
    if (Array.isArray(props.cardImages) && props.cardImages.length) return props.cardImages.length;
  }
  return 1;
}

/** Preview-data met per-order renders (flat_preview) in plaats van de standaard ontwerp-render. */
export function withCoidPreview(previewData: string, coid: string, t = Date.now()): string | null {
  let items: PreviewItem[];
  try {
    items = JSON.parse(previewData) as PreviewItem[];
  } catch {
    return null;
  }
  if (!Array.isArray(items)) return null;
  const foil = foilFrom(items);
  const pages = pageCountFrom(items);
  const cardImages: CardImageUrls[] = [];
  for (let p = 1; p <= pages; p++) {
    const mobile = flatPreviewUrl(coid, p, foil, 512, t);
    const desktop = flatPreviewUrl(coid, p, foil, 1024, t);
    cardImages.push({ mobile: { src: mobile, fallback: mobile }, desktop: { src: desktop, fallback: desktop } });
  }
  let changed = false;
  const next = items.map((item) => {
    if (!item.component || !CARD_COMPONENTS.includes(item.component)) return item;
    changed = true;
    return { ...item, properties: { ...item.properties, cardImages } };
  });
  return changed ? JSON.stringify(next) : null;
}

/**
 * Ververs de PDP-preview na het opslaan met per-order renders (flat_preview). Alleen het
 * `preview-data`-attribuut wijzigt: `<pb-pdp-preview>` rendert daarop opnieuw, en het element
 * blijft staan. Daarin zitten op mobiel de Bewerken-knop en de klik-handler van de light editor.
 * Geen `pdp-editor-saved` sturen: dat event telt als "bewerkt in de light editor" (tracking).
 */
export function refreshCardPreview(coid: string): void {
  const t = Date.now();
  for (const el of [document.getElementById('js-product-builder'), document.querySelector('pb-pdp-preview')]) {
    const data = el?.getAttribute('preview-data');
    if (!el || !data) continue;
    const next = withCoidPreview(data, coid, t);
    if (next) el.setAttribute('preview-data', next);
  }
  log('preview ververst', coid);
}

// De light editor laadt het ontwerp bij de eerste keer openen en daarna alleen bij een andere coid.
// Na een wijziging van buitenaf (de aquarel) toont een eerder geopende editor dus het oude ontwerp,
// en zou zijn autosave de aquarel overschrijven. Daarom laten we hem bij het openen opnieuw laden.
let lightEditorStale = false;

export function markLightEditorStale(): void {
  lightEditorStale = true;
}

/**
 * Eenmalig bij het opstarten. `pdp-editor-opened` komt van de inject, vóór hij de editor toont
 * (via zijn knop, een klik op de preview of onze "Bewerken"). Een coid-wijziging op `<pdp-editor>`
 * laat de editor het ontwerp opnieuw laden; het element opnieuw aanmaken werkt niet (de coid komt
 * dan pas na het initialiseren binnen en de editor blijft op "Loading editor..." staan).
 */
export function reloadLightEditorOnOpen(): void {
  document.addEventListener('pdp-editor-opened', () => {
    if (!lightEditorStale) return;
    lightEditorStale = false;
    const editor = document.querySelector('#pdp-editor-inject-overlay pdp-editor');
    const coid = editor?.getAttribute('coid');
    // Nog nooit geopend: dan laadt de editor het ontwerp toch al vers.
    if (!editor || !coid) return;
    editor.setAttribute('coid', '');
    editor.setAttribute('coid', coid);
    log('light editor laadt het nieuwe ontwerp');
  });
}

/**
 * KC zet bij een optiewissel (folie, hoeken) de standaard ontwerp-render terug in de preview.
 * Na het toepassen houden we de per-order render vast, zodat de aquarel zichtbaar blijft.
 */
export function keepPreviewInSync(getAppliedCoid: () => string | null): () => void {
  let timer: number | undefined;
  const check = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      const coid = getAppliedCoid();
      if (!coid || readCoid() !== coid) return;
      const data = document.querySelector('pb-pdp-preview')?.getAttribute('preview-data') ?? '';
      if (data && !data.includes(`/flat_preview/${encodeURIComponent(coid)}/`)) {
        log('preview teruggezet door KC, aquarel opnieuw tonen');
        refreshCardPreview(coid);
      }
    }, 150);
  };
  const observer = new MutationObserver(check);
  const container = document.querySelector('.pdp-preview') ?? document.querySelector('pb-pdp-preview')?.parentElement;
  if (container) {
    observer.observe(container, { subtree: true, childList: true, attributes: true, attributeFilter: ['preview-data'] });
  }
  check();
  return () => observer.disconnect();
}

/** Zet een al geüploade afbeelding in het fotokader van de order en ververst de preview. */
export async function applyUploaded(coid: string, image: UploadedImage, scope?: AiPhotoScope): Promise<PhotoFrame> {
  const design = await fetchDesign(coid);
  const target = findPhotoFrame(design);
  if (!target) throw new EmbedError('NO_FRAME');
  await saveDesign(coid, applyImageToDesign(design, target, image));
  markApplied(coid, image.sid, scope);
  markLightEditorStale();
  refreshCardPreview(coid);
  return target;
}

// KC maakt bij elke PDP-weergave een nieuwe order (coid). Om de aquarel na herladen niet kwijt te
// raken, onthouden we per ontwerp (URL-slug) de geüploade afbeelding, alleen voor deze browsersessie.
// Never restore the old unscoped session keys: their shop/config cannot be verified.
function sessionKey(kind: 'last' | 'applied', id: string, scope?: AiPhotoScope): string {
  const current = scope ?? originScope(location.origin) ?? { shopId: 'hippe', environmentId: 'production' };
  return `aps:${kind}:v2:${current.shopId}:${current.environmentId}:${normalizeAiPhotoConfigId(current.configId)}:${id}`;
}
const LAST_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export function designSlug(): string {
  return location.pathname.split('/').filter(Boolean).pop() ?? 'onbekend';
}

export function rememberResult(image: UploadedImage, scope?: AiPhotoScope): void {
  try {
    sessionStorage.setItem(sessionKey('last', designSlug(), scope), JSON.stringify({ ...image, at: Date.now() }));
  } catch {
    /* privé-modus: geen probleem */
  }
}

export function lastResult(scope?: AiPhotoScope): UploadedImage | null {
  try {
    const raw = sessionStorage.getItem(sessionKey('last', designSlug(), scope));
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<UploadedImage> & { at?: number };
    if (typeof v.sid !== 'string' || !v.sid || typeof v.width !== 'number' || typeof v.height !== 'number' ||
      !Number.isFinite(v.width) || !Number.isFinite(v.height) || v.width <= 0 || v.height <= 0 ||
      typeof v.at !== 'number' || !Number.isFinite(v.at) || v.at > Date.now() || Date.now() - v.at > LAST_MAX_AGE_MS) return null;
    return { sid: v.sid, width: v.width, height: v.height };
  } catch {
    return null;
  }
}

export function markApplied(coid: string, sid: string, scope?: AiPhotoScope): void {
  try {
    sessionStorage.setItem(sessionKey('applied', coid, scope), JSON.stringify({ sid, at: Date.now() }));
  } catch {
    /* privé-modus: geen probleem */
  }
}

export function isApplied(coid: string, scope?: AiPhotoScope): boolean {
  try {
    const raw = sessionStorage.getItem(sessionKey('applied', coid, scope));
    if (!raw) return false;
    const v = JSON.parse(raw) as { sid?: unknown; at?: number };
    return typeof v.sid === 'string' && !!v.sid && !!v.at && v.at <= Date.now() && Date.now() - v.at <= LAST_MAX_AGE_MS;
  } catch {
    return false;
  }
}
